/**
 * Pre-parse SVG partial-import calls on the main thread.
 *
 * Why this exists
 * ---------------
 * The OpenSCAD compile worker used to run `DOMParser` itself to extract
 * sub-elements out of multi-shape SVGs for `import("file.svg#id")` and
 * `import("file.svg[N]")` calls.  `DOMParser`, however, is **not** available
 * in every browser's Web Worker scope (notably Firefox and older Safari),
 * so the imports silently failed there.
 *
 * Solution: do the parsing on the main thread (where DOMParser is always
 * present), inject the synthesized fragment SVG files into the worker's
 * virtual filesystem via the existing FS_WRITE_BATCH channel, and rewrite
 * the `import("…")` calls to point at the synthesized virtual paths.  The
 * worker then only has to do plain string operations.
 *
 * Single source of truth
 * ----------------------
 * The detection regexes live in `src/utils/svgImportRegex.ts` and are
 * imported here.  The actual element extraction / wrapping helpers live
 * in `src/utils/svgSplitter.ts` and are reused unchanged.
 */

import { SVG_FRAGMENT_RE, SVG_INDEX_RE } from './svgImportRegex';
import {
  extractSvgFragment,
  extractSvgByIndex,
  svgFragmentVirtualPath,
  svgIndexVirtualPath,
} from './svgSplitter';

/** A synthesized fragment SVG ready to be written into the worker's VFS. */
export interface SvgFragmentFile {
  /** Virtual FS path under `/__svg_frag__/`, e.g. "/__svg_frag__/shapes__star.svg". */
  path: string;
  /** Standalone SVG text content (with the original viewBox/attrs preserved). */
  content: string;
}

/** Result of `prepareSvgFragments()`. */
export interface PrepareSvgFragmentsResult {
  /**
   * The SCAD source with every `import("file.svg#id")` and
   * `import("file.svg[N]")` rewritten to `import("/__svg_frag__/…")`.
   * If no fragment imports were found, this is the input string unchanged.
   */
  rewrittenCode: string;
  /**
   * The synthesized fragment SVG files that should be written into the
   * worker's virtual filesystem before compilation.  Empty when the input
   * contained no fragment imports.
   */
  fragmentFiles: SvgFragmentFile[];
}

/**
 * Resolver callback used by `prepareSvgFragments()` to fetch the source SVG
 * text for a referenced filename.  Should return the raw SVG content as a
 * string, or `null` / `undefined` if the file is not in the project.
 */
export type ReadSvgFn = (svgFilename: string) => Promise<string | null | undefined>;

/**
 * Scan `code` for SVG partial-import calls (id-form and index-form), parse
 * the referenced source SVGs with the main-thread DOMParser, and produce:
 *   - a synthesized fragment SVG file for each unique selector, and
 *   - a rewritten copy of the SCAD source where each call points at the
 *     fragment file's virtual path.
 *
 * Errors propagate to the caller and should be surfaced through the same
 * error sink the compile worker uses (so users see the same panel text).
 *
 * Fast path: if the input contains neither form, returns the input unchanged
 * with an empty `fragmentFiles` array — no DOMParser invocation, no copying.
 *
 * @param code     Raw SCAD source text.
 * @param readSvg  Resolver that returns SVG text for a referenced filename,
 *                 or `null`/`undefined` if the file isn't available.
 *
 * @throws Error  When a referenced source SVG is missing from the project,
 *                an id is not found, an index is out of range, or the SVG
 *                fails to parse.  Error messages match those previously
 *                produced inside the worker.
 */
export async function prepareSvgFragments(
  code: string,
  readSvg: ReadSvgFn,
): Promise<PrepareSvgFragmentsResult> {
  // Reset lastIndex defensively — these are module-level regexes shared
  // across callers.
  SVG_FRAGMENT_RE.lastIndex = 0;
  SVG_INDEX_RE.lastIndex = 0;

  const hasFragment = SVG_FRAGMENT_RE.test(code);
  SVG_FRAGMENT_RE.lastIndex = 0;
  const hasIndex = SVG_INDEX_RE.test(code);
  SVG_INDEX_RE.lastIndex = 0;

  if (!hasFragment && !hasIndex) {
    return { rewrittenCode: code, fragmentFiles: [] };
  }

  // Map from original import path (as it appears in SCAD) → virtual FS path.
  const rewrites = new Map<string, string>();
  const fragmentFiles: SvgFragmentFile[] = [];
  // Dedupe by virtual path so two imports of the same selector share one file.
  const emittedPaths = new Set<string>();

  // Cache decoded SVG text per source filename — multiple selectors against
  // the same SVG should only fetch + decode it once.
  const sourceCache = new Map<string, string>();
  const fetchSource = async (
    svgFile: string,
    fullPath: string,
  ): Promise<string> => {
    const cached = sourceCache.get(svgFile);
    if (cached !== undefined) return cached;
    const content = await readSvg(svgFile);
    if (content == null) {
      throw new Error(
        `import("${fullPath}"): source file "${svgFile}" not found in project`,
      );
    }
    sourceCache.set(svgFile, content);
    return content;
  };

  // --- id-fragment form: import("file.svg#id") -------------------------------
  if (hasFragment) {
    const seen = new Set<string>();
    let m: RegExpExecArray | null;
    while ((m = SVG_FRAGMENT_RE.exec(code)) !== null) {
      const fullPath = m[1]; // e.g. "shapes.svg#star"
      if (seen.has(fullPath)) continue;
      seen.add(fullPath);

      const hashIdx = fullPath.indexOf('#');
      const svgFile = fullPath.slice(0, hashIdx);
      const id = fullPath.slice(hashIdx + 1);

      const svgContent = await fetchSource(svgFile, fullPath);
      let fragSvg: string;
      try {
        fragSvg = extractSvgFragment(svgContent, id);
      } catch (err) {
        // Re-throw with the original-import-path prefix so users see the
        // SCAD-level call, not just the underlying parse/lookup error.
        const msg = err instanceof Error ? err.message : String(err);
        throw new Error(`import("${fullPath}"): ${msg}`);
      }

      const virtualPath = svgFragmentVirtualPath(svgFile, id);
      rewrites.set(fullPath, virtualPath);
      if (!emittedPaths.has(virtualPath)) {
        emittedPaths.add(virtualPath);
        fragmentFiles.push({ path: virtualPath, content: fragSvg });
      }
    }
    SVG_FRAGMENT_RE.lastIndex = 0;
  }

  // --- index form: import("file.svg[N]") -------------------------------------
  if (hasIndex) {
    const seen = new Set<string>();
    let m: RegExpExecArray | null;
    while ((m = SVG_INDEX_RE.exec(code)) !== null) {
      const svgFile = m[1]; // e.g. "shapes.svg"
      const indexStr = m[2]; // e.g. "2"
      const fullPath = `${svgFile}[${indexStr}]`;
      if (seen.has(fullPath)) continue;
      seen.add(fullPath);

      const index = parseInt(indexStr, 10);
      const svgContent = await fetchSource(svgFile, fullPath);
      let fragSvg: string;
      try {
        fragSvg = extractSvgByIndex(svgContent, index);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        throw new Error(`import("${fullPath}"): ${msg}`);
      }

      const virtualPath = svgIndexVirtualPath(svgFile, index);
      rewrites.set(fullPath, virtualPath);
      if (!emittedPaths.has(virtualPath)) {
        emittedPaths.add(virtualPath);
        fragmentFiles.push({ path: virtualPath, content: fragSvg });
      }
    }
    SVG_INDEX_RE.lastIndex = 0;
  }

  // Rewrite both forms in the source.  We use the same regexes — the
  // replacement callback looks up the virtual path keyed by the captured
  // selector, so the rewrites are deterministic and order-independent.
  let rewrittenCode = code;
  if (hasFragment) {
    rewrittenCode = rewrittenCode.replace(SVG_FRAGMENT_RE, (match, path) => {
      const vp = rewrites.get(path);
      return vp ? `import("${vp}")` : match;
    });
    SVG_FRAGMENT_RE.lastIndex = 0;
  }
  if (hasIndex) {
    rewrittenCode = rewrittenCode.replace(
      SVG_INDEX_RE,
      (match, svgFile, indexStr) => {
        const key = `${svgFile}[${indexStr}]`;
        const vp = rewrites.get(key);
        return vp ? `import("${vp}")` : match;
      },
    );
    SVG_INDEX_RE.lastIndex = 0;
  }

  return { rewrittenCode, fragmentFiles };
}

/**
 * Build a `ReadSvgFn` resolver from a list of WorkerFile-like objects.
 *
 * Project files arriving from the editor have `filename` + `content`
 * (string or ArrayBuffer).  This helper looks them up by filename and
 * decodes binary content as UTF-8 text.
 *
 * Returns a resolver that yields `null` for unknown filenames.
 */
export function makeWorkerFileSvgResolver(
  files: ReadonlyArray<{
    filename: string;
    content: string | ArrayBuffer;
    isBinary?: boolean;
  }>,
): ReadSvgFn {
  return async (svgFilename: string) => {
    const f = files.find((x) => x.filename === svgFilename);
    if (!f) return null;
    if (typeof f.content === 'string') return f.content;
    return new TextDecoder().decode(f.content);
  };
}
